// src/services/nearbyHealthcareService.js
import supabase from '@/services/supabaseClient';

/** ---------- small helpers ---------- */
const toNum = (v) => (typeof v === 'number' ? v : Number(v));
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

/** Haversine distance in KM */
function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

/** Try a query on a table; if it errors, return {error}. */
async function tryTableSelect(table, columns = '*', limit = 1) {
  try {
    const { data, error } = await supabase.from(table).select(columns).limit(limit);
    if (error) return { error };
    return { data };
  } catch (e) {
    return { error: e };
  }
}

/** Pick available table + column schema; prefer health_facilities */
async function resolveSchema() {
  // try modern table
  let t = 'health_facilities';
  let cols = 'id,name,type,lat,lng,latitude,longitude,city,country,services,hours,verified';
  let res = await tryTableSelect(t, cols, 1);
  if (!res.error) {
    return { table: t, schema: 'modern' }; // lat/lng or latitude/longitude
  }
  // fallback to legacy table
  t = 'healthcare_locations';
  res = await tryTableSelect(t, cols, 1);
  if (!res.error) return { table: t, schema: 'legacy' };
  // nothing available
  return { table: null, schema: null };
}

/** Normalize a row into the shape we use everywhere */
function normalizeRow(row) {
  const lat = toNum(row?.lat ?? row?.latitude);
  const lng = toNum(row?.lng ?? row?.longitude);
  return {
    id: row?.id,
    name: row?.name,
    type: row?.type,
    latitude: lat,
    longitude: lng,
    city: row?.city || null,
    country: row?.country || null,
    services: row?.services || [],
    hours: row?.hours || null,
    verified: typeof row?.verified === 'boolean' ? row.verified : true,
    // keep original
    _raw: row,
  };
}

/** ---------- Service ---------- */
class NearbyHealthcareService {
  /**
   * Prefer RPC if available; otherwise fallback to table query + client-side filter.
   */
  async findNearbyHealthcare(latitude, longitude, type = null, maxDistanceKm = 50) {
    try {
      // 1) Try RPC first
      try {
        const { data, error } = await supabase.rpc('find_nearby_healthcare', {
          user_lat: latitude,
          user_lon: longitude,
          location_type: type,
          max_distance: maxDistanceKm,
        });
        if (!error && Array.isArray(data)) {
          const normalized = data.map(normalizeRow);
          return { data: normalized, error: null };
        }
        // if the function exists but errored for other reasons, bubble up below
        if (error && !String(error?.message || '').includes('function find_nearby_healthcare')) {
          console.error('RPC find_nearby_healthcare error:', error);
        }
      } catch (e) {
        // swallow and fallback to table
      }

      // 2) Table fallback
      const { table } = await resolveSchema();
      if (!table) {
        return { data: [], error: 'No healthcare facilities table found in Supabase.' };
      }

      // fetch a reasonable chunk near the user (coarse bbox to reduce data)
      // ~1 deg lat ≈ 111km; build bbox from maxDistanceKm
      const dDeg = clamp(maxDistanceKm / 111, 0.05, 2.0);
      const minLat = latitude - dDeg;
      const maxLat = latitude + dDeg;
      const minLng = longitude - dDeg;
      const maxLng = longitude + dDeg;

      // We can’t do geospatial without PostGIS here; pull capped list in bbox-ish using ilt filters if columns available.
      let q = supabase
        .from(table)
        .select('id,name,type,lat,lng,latitude,longitude,city,country,services,hours,verified')
        .limit(1000);

      // Some rows may have only lat/lng or only latitude/longitude; we can’t filter server-side reliably → pull & filter client-side.
      const { data, error } = await q;
      if (error) {
        console.error('Table fallback error:', error);
        return { data: [], error: error?.message || 'Failed to query facilities.' };
      }

      const filtered = (data || [])
        .map(normalizeRow)
        .filter((r) => {
          if (typeof r.latitude !== 'number' || typeof r.longitude !== 'number') return false;
          if (type && r.type !== type) return false;
          // quick bbox filter
          if (r.latitude < minLat || r.latitude > maxLat) return false;
          if (r.longitude < minLng || r.longitude > maxLng) return false;
          // precise circle filter
          const dist = haversineKm(latitude, longitude, r.latitude, r.longitude);
          return dist <= maxDistanceKm;
        });

      return { data: filtered, error: null };
    } catch (err) {
      console.error('findNearbyHealthcare fatal:', err);
      return {
        data: [],
        error: 'Unable to connect to location services. Please check your internet connection and try again.',
      };
    }
  }

  /** Browser geolocation */
  async getCurrentLocation(options = { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }) {
    return new Promise((resolve) => {
      if (!navigator?.geolocation) {
        resolve({
          latitude: null,
          longitude: null,
          error: 'Geolocation is not supported by this browser.',
        });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            error: null,
          });
        },
        (error) => {
          let msg = 'An unknown error occurred while retrieving location.';
          if (error?.code === error.PERMISSION_DENIED) msg = 'Location access denied by user. Please enable location permissions.';
          if (error?.code === error.POSITION_UNAVAILABLE) msg = 'Location information is unavailable.';
          if (error?.code === error.TIMEOUT) msg = 'Location request timed out. Please try again.';
          resolve({ latitude: null, longitude: null, error: msg });
        },
        options
      );
    });
  }

  /** Text search (works for either table name) */
  async searchHealthcareProviders(query, type = null) {
    try {
      const { table } = await resolveSchema();
      if (!table) return { data: [], error: 'No healthcare facilities table found.' };

      let qb = supabase.from(table).select('id,name,type,lat,lng,latitude,longitude,city,country,services,hours,verified');

      if (query?.trim()) {
        // name/address may not exist in modern schema; we search name + city
        qb = qb.or(`name.ilike.%${query}%,city.ilike.%${query}%`);
      }
      if (type) qb = qb.eq('type', type);

      const { data, error } = await qb.order('name', { ascending: true }).limit(100);
      if (error) {
        console.error('Search error:', error);
        return { data: [], error: error?.message };
      }
      return { data: (data || []).map(normalizeRow), error: null };
    } catch (e) {
      console.error('Search fatal:', e);
      return { data: [], error: 'Unable to search healthcare providers.' };
    }
  }

  /** Details by id (any table) */
  async getHealthcareProviderDetails(providerId) {
    try {
      const { table } = await resolveSchema();
      if (!table) return { data: null, error: 'No healthcare facilities table found.' };
      const { data, error } = await supabase
        .from(table)
        .select('id,name,type,lat,lng,latitude,longitude,city,country,services,hours,verified')
        .eq('id', providerId)
        .single();
      if (error) {
        console.error('Details error:', error);
        return { data: null, error: error?.message };
      }
      return { data: normalizeRow(data), error: null };
    } catch (e) {
      console.error('Details fatal:', e);
      return { data: null, error: 'Unable to load provider details.' };
    }
  }

  /** Types list for filters */
  getHealthcareTypes() {
    return [
      { value: 'hospital',  label: 'Hospitals',          icon: 'Building2' },
      { value: 'clinic',    label: 'Clinics',            icon: 'Cross' },
      { value: 'pharmacy',  label: 'Pharmacies',         icon: 'Pill' },
      { value: 'lab',       label: 'Laboratories',       icon: 'TestTube' },
      { value: 'emergency', label: 'Emergency Services', icon: 'Ambulance' },
    ];
  }

  /** Utilities preserved from your file */
  calculateDistance(lat1, lon1, lat2, lon2) {
    return Math.round(haversineKm(lat1, lon1, lat2, lon2) * 100) / 100;
  }

  formatProviderHours(hours) {
    if (!hours || Object.keys(hours)?.length === 0) return 'Hours not available';
    const today = new Date().getDay();
    const days = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
    const th = hours?.[days[today]];
    if (!th) return 'Hours not available';
    if (th?.closed) return 'Closed today';
    return `Today: ${th?.open} - ${th?.close}`;
  }

  isProviderOpen(hours) {
    if (!hours || Object.keys(hours)?.length === 0) return false;
    const now = new Date();
    const days = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
    const th = hours?.[days[now.getDay()]];
    if (!th || th?.closed) return false;
    const num = (h) => parseInt(String(h || '').replace(':', ''), 10);
    const open = num(th.open);
    const close = num(th.close);
    const cur = now.getHours() * 100 + now.getMinutes();
    return cur >= open && cur <= close;
  }

  getProviderServices(services) {
    if (!Array.isArray(services) || !services.length) return ['General Services'];
    return services;
  }

  /** High-level helper used by the map layer */
  async getNearbyFacilities(params) {
    try {
      const { latitude, longitude, radius = 10000, limit = 20, type = null } = params;
      const km = radius / 1000;
      const { data, error } = await this.findNearbyHealthcare(latitude, longitude, type, km);
      if (error) return { data: [], error };
      const enhanced = (data || [])
        .map((f) => {
          const distKm = haversineKm(latitude, longitude, f.latitude, f.longitude);
          return {
            ...f,
            distance: Math.round(distKm * 1000), // meters
            formattedDistance: `${distKm.toFixed(1)}km`,
            isOpen: this.isProviderOpen(f.hours),
            formattedHours: this.formatProviderHours(f.hours),
            services: this.getProviderServices(f.services),
          };
        })
        .sort((a, b) => a.distance - b.distance)
        .slice(0, limit);
      return { data: enhanced, error: null };
    } catch (e) {
      console.error('getNearbyFacilities fatal:', e);
      return { data: [], error: 'Unable to load nearby facilities.' };
    }
  }

  /** City list fetch (for dropdowns / multi-level location pickers) */
  async getFacilitiesInCity(city) {
    try {
      const { table } = await resolveSchema();
      if (!table) return { data: [], error: 'No healthcare facilities table found.' };
      const { data, error } = await supabase
        .from(table)
        .select('id,name,type,lat,lng,latitude,longitude,city,country,services,hours,verified')
        .eq('city', city)
        .limit(500);
      if (error) return { data: [], error: error?.message };
      return { data: (data || []).map(normalizeRow), error: null };
    } catch (e) {
      console.error('getFacilitiesInCity fatal:', e);
      return { data: [], error: 'Unable to load facilities for city.' };
    }
  }
}

export const nearbyHealthcareService = new NearbyHealthcareService();
/** keep default export too so existing imports keep working */
export default nearbyHealthcareService;